import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const WhatYouGet = () => {
  const headerRef = useScrollAnimation({ delay: 0 });
  const item0Ref = useScrollAnimation({ delay: 0 });
  const item1Ref = useScrollAnimation({ delay: 100 });
  const item2Ref = useScrollAnimation({ delay: 200 });
  const item3Ref = useScrollAnimation({ delay: 300 });
  const item4Ref = useScrollAnimation({ delay: 400 });
  const item5Ref = useScrollAnimation({ delay: 500 });
  const item6Ref = useScrollAnimation({ delay: 600 });
  const item7Ref = useScrollAnimation({ delay: 700 });

  const deliverables = [
    {
      icon: 'ri-file-list-3-line',
      title: 'Custom Workout Plan',
      description: 'Personalized training program designed for your goals, fitness level, and schedule',
      ref: item0Ref,
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Nutrition Coaching',
      description: 'Macro based meal guidance with flexible eating strategies that fit your lifestyle',
      ref: item1Ref,
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Accountability Check Ins',
      description: 'Regular progress reviews to keep you on track and motivated every step of the way',
      ref: item2Ref,
    },
    {
      icon: 'ri-video-line',
      title: 'Exercise Video Library',
      description: 'Access to 200+ professional exercise demonstration videos with proper form cues',
      ref: item3Ref,
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Progress Tracking',
      description: 'Advanced metrics dashboard to monitor your weight, measurements, and strength gains',
      ref: item4Ref,
    },
    {
      icon: 'ri-message-3-line',
      title: '24/7 Support Access',
      description: 'Direct messaging support for questions, form checks, and motivation when you need it',
      ref: item5Ref,
    },
    {
      icon: 'ri-book-open-line',
      title: 'Recipe Database',
      description: '500+ healthy recipes with macros calculated, meal prep guides, and shopping lists',
      ref: item6Ref,
    },
    {
      icon: 'ri-community-line',
      title: 'Private Community',
      description: 'Exclusive access to our members only group for support, challenges, and accountability',
      ref: item7Ref,
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-4">
            What's Inside Every Package
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
            No matter which tier you choose, you get access to our complete transformation system
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {deliverables.map((item, index) => (
            <div
              key={index}
              ref={item.ref}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-gradient-to-br from-[#B8860B] to-[#8B0000] mb-4">
                <i className={`${item.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProgramsComparison = () => {
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const comparisonHeaderRef = useScrollAnimation({ delay: 0 });
  const tableRef = useScrollAnimation({ delay: 100 });
  const ctaRef = useScrollAnimation({ delay: 200 });

  const features = [
    { name: 'Custom Workout Plan',             foundation: true,             ascend: true,              elite: true },
    { name: 'Nutrition Blueprint',             foundation: 'Basic',          ascend: 'Advanced Macros',  elite: 'Premium 1 on 1' },
    { name: 'Check Ins',                       foundation: 'Bi Weekly',      ascend: 'Weekly',           elite: 'Weekly and On Demand' },
    { name: 'Progress Tracking Dashboard',     foundation: true,             ascend: true,              elite: true },
    { name: 'Exercise Video Library',          foundation: true,             ascend: true,              elite: true },
    { name: 'Recipe Database Access',          foundation: true,             ascend: true,              elite: true },
    { name: 'Private Community Access',        foundation: true,             ascend: true,              elite: true },
    { name: 'Direct Message Support',          foundation: false,            ascend: true,              elite: true },
    { name: 'Form Review and Video Feedback',  foundation: false,            ascend: true,              elite: true },
    { name: '1 on 1 Weekly Video Calls',       foundation: false,            ascend: false,             elite: true },
    { name: 'Supplement Guidance',             foundation: false,            ascend: true,              elite: true },
    { name: 'Bloodwork Guidance',              foundation: false,            ascend: false,             elite: true },
    { name: 'Meal Plan Templates',             foundation: '3 Templates',    ascend: '10 Templates',    elite: 'Unlimited Custom' },
    { name: 'Workout Plan Updates',            foundation: 'Monthly',        ascend: 'Bi Weekly',       elite: 'Weekly' },
    { name: 'Mindset Training Modules',        foundation: false,            ascend: true,              elite: true },
    { name: 'Lifestyle Optimization Plan',     foundation: false,            ascend: true,              elite: true },
    { name: 'Executive Performance Coaching',  foundation: false,            ascend: false,             elite: true },
    { name: 'Stress and Recovery Optimization',foundation: false,            ascend: false,             elite: true },
    { name: 'Priority Access and Scheduling',  foundation: false,            ascend: false,             elite: true },
    { name: 'VIP Private Access Channel',      foundation: false,            ascend: false,             elite: true },
    { name: 'Quarterly Performance Reset',     foundation: false,            ascend: false,             elite: true },
  ];

  const packages = [
    { name: 'Foundation',    key: 'foundation', price: '$297/mo',   color: 'from-[#B8860B] to-[#a07608]' },
    { name: 'Ascend',        key: 'ascend',     price: '$597/mo',   color: 'from-[#8B0000] to-[#6b0000]' },
    { name: 'Legacy Elite',  key: 'elite',      price: '$1,500/mo', color: 'from-black to-[#8B0000]' },
  ];

  const renderValue = (value: boolean | string) => {
    if (value === true) return <i className="ri-check-line text-xl" style={{ color: '#B8860B' }}></i>;
    if (value === false) return <i className="ri-close-line text-xl" style={{ color: '#cccccc' }}></i>;
    return <span className="text-xs font-semibold text-black">{value}</span>;
  };

  return (
    <>
      <WhatYouGet />

      <section className="py-16 md:py-24" style={{ background: '#f9f6f0' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={comparisonHeaderRef} className="text-center mb-12 md:mb-16">
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#B8860B' }}>
              Side-by-Side Breakdown
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-4">
              Compare All Packages
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              See exactly what you get with each tier and choose the perfect fit for your goals.
            </p>
          </div>

          {/* Desktop Table */}
          <div ref={tableRef} className="hidden lg:block overflow-x-auto rounded-2xl shadow-lg">
            <table className="w-full bg-white overflow-hidden">
              <thead>
                <tr style={{ background: 'linear-gradient(90deg, #1a1a1a, #8B0000)' }}>
                  <th className="px-6 py-5 text-left text-white font-bold text-sm uppercase tracking-wider w-1/4">
                    Feature
                  </th>
                  {packages.map((pkg) => (
                    <th key={pkg.key} className="px-6 py-5 text-center text-white font-bold">
                      <div className="text-base font-extrabold">{pkg.name}</div>
                      <div className="text-xs font-semibold mt-1 opacity-80">{pkg.price}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr
                    key={index}
                    style={{ background: index % 2 === 0 ? '#ffffff' : '#faf8f4' }}
                  >
                    <td className="px-6 py-4 text-sm font-semibold text-black border-b" style={{ borderColor: '#f0ece4' }}>
                      {feature.name}
                    </td>
                    {packages.map((pkg) => (
                      <td key={pkg.key} className="px-6 py-4 text-center border-b" style={{ borderColor: '#f0ece4' }}>
                        {renderValue(feature[pkg.key as keyof typeof feature] as boolean | string)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Accordion */}
          <div className="lg:hidden space-y-4">
            {packages.map((pkg, pkgIndex) => (
              <div key={pkg.key} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => setActiveAccordion(activeAccordion === pkgIndex ? null : pkgIndex)}
                  className={`w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r ${pkg.color} text-white cursor-pointer`}
                >
                  <div>
                    <span className="font-extrabold text-base">{pkg.name}</span>
                    <span className="ml-3 text-xs opacity-80">{pkg.price}</span>
                  </div>
                  <i className={`ri-arrow-${activeAccordion === pkgIndex ? 'up' : 'down'}-s-line text-xl`}></i>
                </button>
                {activeAccordion === pkgIndex && (
                  <div className="p-4 space-y-2">
                    {features.map((feature, featureIndex) => {
                      const value = feature[pkg.key as keyof typeof feature];
                      return (
                        <div
                          key={featureIndex}
                          className="flex items-center justify-between py-2 border-b last:border-0"
                          style={{ borderColor: '#f0ece4' }}
                        >
                          <span className="text-sm font-medium text-black flex-1">{feature.name}</span>
                          <div className="flex-shrink-0 ml-4">
                            {renderValue(value as boolean | string)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div ref={ctaRef} className="mt-12 text-center">
            <p className="text-base text-gray-600 mb-6">
              Still not sure which package is right for you?
            </p>
            <button
              onClick={() => {
                const el = document.getElementById('booking-form');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-4 font-bold rounded-lg text-white transition-all duration-300 hover:shadow-xl whitespace-nowrap cursor-pointer"
              style={{ background: 'linear-gradient(90deg, #B8860B, #8B0000)' }}
            >
              Book a Free Consultation Call
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProgramsComparison;